import type { IPackagingOriginInfoProps } from 'components/PackagingOriginInfo';
import ClientProductSlugPage, {
  type ProductCategory,
  type SubCategory,
} from 'components/PageLevelComponents/ClientProductSlugPage';
import type { IProductNutrition } from 'components/ProductNutrition';
import type { Product, ProductCMSData } from 'types/Product';
import type { RecipeData, RecipeResult } from 'types/Recipes';

import { Layout } from '@/components/Layout/Layout';
import { ProductDetailSchema } from '@/schema/products/p/ProductDetailSchema';
import { getPathTranslation } from '@/service/localization/getPathTranslations';
import type { ISocialMediaComponent } from 'components/SocialMedia/types';
import { ANONYMOUS_TOKEN_NAME, LOCALE_DEFAULT } from 'config/config';
import type { GetServerSideProps } from 'next';
import { ProductPageController } from 'server/controller/page/products/productPageController';
import type { IProductPageResource } from 'server/resource/page/productPageResource';

const RootPage = (props: IProductPageResource) => {
  const {
    metaData,
    product,
    nutritionData,
    breadcrumbsCMSData,
    footerNavData,
    footerImage,
    locale,
    searchOverlayData,
    countrySelectorData,
    newsletterData,
    contactUsUrl,
    difficultyCopy,
    getHeaderNav,
    getMobileNavData,
    popularSearchData,
    cardCarouselCMSData,
    recallData,
    productDescription,
    sustainabilityData,
    cmsData,
    recipeData,
    followUsData,
    socialShareData,
    subCategory,
    category,
    subCategorySlugName,
    subCategoryProductLength,
    teasersLarge,
    relatedRecipes,
    teasersSmall,
    pathTranslations,
    userToken,
    globalDictionary,
    currentUrl,
  } = props;

  return (
    <Layout
      themeName={undefined}
      pathTranslations={pathTranslations}
      locale={locale}
      globalMetadata={metaData}
      getHeaderNav={getHeaderNav}
      getMobileNavData={getMobileNavData}
      popularSearchData={popularSearchData}
      footerNavData={footerNavData}
      footerImage={footerImage}
      searchOverlayData={searchOverlayData}
      countrySelectorData={countrySelectorData}
      newsletterData={newsletterData}
      contactUsUrl={contactUsUrl}
      difficultyCopy={difficultyCopy as Record<string, string>}
      cardCarouselCMSData={cardCarouselCMSData}
      recallData={recallData}
      userToken={userToken}
      globalDictionary={globalDictionary}
    >
      <ProductDetailSchema
        product={product}
        nutritionData={nutritionData}
        currentUrl={currentUrl}
      />
      <ClientProductSlugPage
        product={product as Product}
        nutritionData={nutritionData as unknown as IProductNutrition}
        productDescription={productDescription as string}
        sustainabilityData={sustainabilityData as IPackagingOriginInfoProps}
        cmsData={cmsData as ProductCMSData}
        recipeData={recipeData as unknown as RecipeData}
        locale={locale}
        followUsData={followUsData as unknown as ISocialMediaComponent}
        socialShareData={socialShareData}
        subCategory={subCategory as unknown as SubCategory}
        category={category as unknown as ProductCategory}
        subCategorySlugName={subCategorySlugName as string}
        subCategoryProductLength={subCategoryProductLength as number}
        relatedRecipes={relatedRecipes as RecipeResult[]}
        breadcrumbsCMSData={breadcrumbsCMSData}
        teasersLarge={teasersLarge}
        teasersSmall={teasersSmall}
      />
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  params,
  defaultLocale,
  resolvedUrl,
  req,
}) => {
  const anonymousToken = req.cookies[ANONYMOUS_TOKEN_NAME];
  const host = req?.headers.host || '';
  const protocol = (req.headers['x-forwarded-proto'] as string) || 'https';
  const translatedPart = getPathTranslation(locale!, 'products');
  const currentUrl = `${protocol}://${host}${resolvedUrl.replace('products', translatedPart)}`;
  const payload = await ProductPageController.getCachedProductPageData({
    resolvedUrl,
    locale: locale || defaultLocale || LOCALE_DEFAULT,
    slug: Array.isArray(params?.slug) ? params.slug.join('/') : (params?.slug ?? ''),
    userToken: anonymousToken ?? '',
  });
  return {
    ...payload,
    props: {
      ...payload.props,
      currentUrl,
    },
  };
};

export default RootPage;
